import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';

import api from '../../../../api/axios';
import { IApiResponseError, IApiState } from '../../../../types/api';
import { RootState } from '../../..';
import { getApiErrorMessage, getSavingsAuthConfig } from '../utils';

interface IMakeWithdrawalRequest {
  savingsAccountNumber: string;
  amount: number;
  isFullWithdrawal: boolean;
  pin?: string;
}

interface IMakeWithdrawalResponse {
  status?: boolean;
  message?: string;
  data?: { id: string };
  errors?: IApiResponseError[];
}

const initialState: IApiState<IMakeWithdrawalResponse> = {
  value: null,
  status: 'idle',
  error: '',
};

export const makeWithdrawalAsync = createAsyncThunk(
  'savings/makeWithdrawal',
  async (
    values: IMakeWithdrawalRequest,
    { rejectWithValue, getState },
  ) => {
    try {
      const state = getState() as RootState;

      const { data } = await api.post<IMakeWithdrawalResponse>(
        '/savings/withdrawals',
        values,
        getSavingsAuthConfig(state),
      );

      return data;
    } catch (err) {
      const error = err as AxiosError<IMakeWithdrawalResponse>;
      return rejectWithValue(error.response?.data);
    }
  },
);

export const makeWithdrawalSlice = createSlice({
  name: 'savings/makeWithdrawal',
  initialState,
  reducers: {
    resetMakeWithdrawal(state) {
      state.value = null;
      state.status = 'idle';
      state.error = '';
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(makeWithdrawalAsync.pending, (state) => {
        state.status = 'loading';
        state.error = '';
      })
      .addCase(makeWithdrawalAsync.fulfilled, (state, action) => {
        state.status = 'success';
        state.value = action.payload as IMakeWithdrawalResponse;
      })
      .addCase(makeWithdrawalAsync.rejected, (state, action) => {
        state.status = 'failed';
        state.value = action.payload as IMakeWithdrawalResponse;
        state.error = getApiErrorMessage(action.payload);
      });
  },
});

export const { resetMakeWithdrawal } = makeWithdrawalSlice.actions;

export default makeWithdrawalSlice.reducer;
